import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Modal,
  notification,
  Form,
  Select,
  Input,
  Avatar,
  Descriptions,
  Tag,
} from "lib/ui";
import { PlusOutlined } from "lib/icons";
import {
  Eye,
  Pencil,
  Trash2,
  Printer,
  Search,
  ShoppingCart,
  Wallet,
  Package,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import AddForm from "./Add";
import moment from "moment";
import { AuthContext } from "../../../context/Context";
import { convertToReadableDate } from "../../Data/Data";
import ApiPath from "../../../hooks/ApiFetch";
import {
  PeopleDataTable,
  ActionBtn,
} from "../../../components/admin/PeopleDataTable";

const { Option } = Select;

const STATUS_FILTERS = [
  { id: "all", label: "All orders" },
  { id: "active", label: "Active" },
  { id: "completed", label: "Completed" },
  { id: "cancelled", label: "Cancelled" },
];

function formatAED(n) {
  return Number(n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function statusBadge(status) {
  const s = (status || "active").toLowerCase();
  const styles = {
    completed: "bg-emerald-50 text-emerald-800 ring-emerald-200/80",
    cancelled: "bg-red-50 text-red-800 ring-red-200/80",
    active: "bg-violet-50 text-violet-800 ring-violet-200/80",
  };
  const cls = styles[s] || styles.active;
  const label = status
    ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
    : "Active";
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${cls}`}
    >
      {label}
    </span>
  );
}

function itemThumbnails(items) {
  if (!items || typeof items !== "object") {
    return <span className="text-xs text-slate-400">—</span>;
  }
  const entries = Object.entries(items);
  if (!entries.length) return <span className="text-xs text-slate-400">—</span>;
  return (
    <Avatar.Group
      maxCount={3}
      size="default"
      maxStyle={{ color: "white", backgroundColor: "#7c3aed" }}
    >
      {entries.map(([id, item]) => (
        <Avatar
          key={id}
          src={item.image}
          alt={item.product}
          style={{ width: 36, height: 36, objectFit: "cover" }}
        />
      ))}
    </Avatar.Group>
  );
}

const OrdersPage = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(undefined);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [form] = Form.useForm();
  const [searchId, setSearchId] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const {
    Users,
    order: data,
    fetchOrder,
    setorders: setData,
  } = useContext(AuthContext);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const handleAddUser = () => {
    setEditingUser(undefined);
    form.resetFields();
    form.setFieldsValue({ items: [{}] });
    setIsModalVisible(true);
  };

  const handleEditUser = useCallback(
    (user) => {
      setEditingUser(user);
      form.setFieldsValue(user);
      setIsModalVisible(true);
    },
    [form]
  );

  const handleViewOrder = useCallback((order) => {
    setViewingOrder(order);
    setIsViewModalVisible(true);
  }, []);

  const handleDeleteUser = useCallback(
    (id) => {
      Modal.confirm({
        title: "Delete this order?",
        content:
          "This cannot be undone. The customer record is not affected.",
        okText: "Delete",
        cancelText: "Cancel",
        okType: "danger",
        onOk: async () => {
          try {
            await ApiPath.delete(`/orders/${id}`);
            setData((prevData) => prevData.filter((item) => item._id !== id));
            notification.success({
              message: "Order deleted",
              description: "The order was removed successfully.",
            });
          } catch {
            notification.error({
              message: "Delete failed",
              description: "Could not delete the order. Try again.",
            });
          }
        },
      });
    },
    [setData]
  );

  const handleCancel = () => {
    setEditingUser(undefined);
    form.resetFields();
    setIsModalVisible(false);
  };

  const handleViewCancel = () => {
    setIsViewModalVisible(false);
    setViewingOrder(null);
  };

  const handleOk = async (values) => {
    try {
      const updatedStages =
        editingUser?.stage?.map((stage) => {
          if (stage.title === values.stagenow) {
            return {
              ...stage,
              completed: true,
              date: new Date().toISOString(),
            };
          }
          return stage;
        }) || [];

      const payload = { ...values };
      delete payload._id;
      delete payload.__v;

      const dataToSend = {
        ...payload,
        stage: updatedStages.length
          ? updatedStages
          : editingUser?.stage || payload.stage,
      };

      if (editingUser?._id) {
        const { data: saved } = await ApiPath.put(
          `/orders/${editingUser._id}`,
          dataToSend
        );
        setData((prevData) =>
          prevData.map((item) =>
            item._id === editingUser._id ? { ...item, ...saved } : item
          )
        );
        notification.success({
          message: "Order updated",
          description: "Changes were saved successfully.",
        });
      } else {
        const response = await ApiPath.post("/orders", values);
        setData((prevData) => [
          ...prevData,
          { ...values, _id: response.data._id },
        ]);
        notification.success({
          message: "Order added",
          description: "New order created.",
        });
      }
      setIsModalVisible(false);
      form.resetFields();
    } catch {
      notification.error({
        message: "Request failed",
        description:
          "Could not save the order. Check your connection and try again.",
      });
    }
  };

  const getName = (_id) => {
    const user = Users?.find((u) => u._id === _id);
    return user?.name || user?.firstname || "Unknown";
  };

  const filteredData = useMemo(() => {
    let list = [...(data || [])]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .filter((item) => item?.position !== "driver")
      .filter((item) =>
        searchId ? String(item.ref ?? "").includes(searchId.trim()) : true
      );
    if (statusFilter !== "all") {
      list = list.filter(
        (item) => (item.status || "").toLowerCase() === statusFilter
      );
    }
    return list;
  }, [data, searchId, statusFilter]);

  const stats = useMemo(() => {
    const base = (data || []).filter((o) => o?.position !== "driver");
    const revenue = filteredData.reduce(
      (s, o) => s + Number(o.totalPrice ?? 0),
      0
    );
    const unpaid = filteredData.filter((o) => Number(o.balance) > 0).length;
    const completed = filteredData.filter(
      (o) => (o.status || "").toLowerCase() === "completed"
    ).length;
    return {
      total: base.length,
      revenue,
      unpaid,
      completed,
    };
  }, [data, filteredData]);

  const orderColumns = [
    {
      key: "ref",
      header: "Ref",
      render: (row) => (
        <button
          type="button"
          onClick={() => handleEditUser(row)}
          className="font-mono text-sm font-bold tabular-nums text-brand-dark hover:text-brand hover:underline"
        >
          #{row.ref ?? "—"}
        </button>
      ),
    },
    {
      key: "customer",
      header: "Customer",
      render: (row) => (
        <span className="font-medium text-slate-900">{getName(row.userId)}</span>
      ),
    },
    {
      key: "items",
      header: "Items",
      render: (row) => itemThumbnails(row.items),
    },
    {
      key: "total",
      header: "Total",
      align: "right",
      render: (row) => (
        <span className="tabular-nums font-semibold text-slate-900">
          {formatAED(row.totalPrice)}{" "}
          <span className="font-normal text-slate-400">AED</span>
        </span>
      ),
    },
    {
      key: "pickup",
      header: "Pick-up",
      render: (row) => (
        <span className="text-xs text-slate-600">
          {convertToReadableDate(row.pickTime)}
        </span>
      ),
    },
    {
      key: "delivery",
      header: "Delivery",
      render: (row) => (
        <span className="text-xs text-slate-600">
          {convertToReadableDate(row.deliveryDate)}
        </span>
      ),
    },
    {
      key: "balance",
      header: "Balance",
      align: "right",
      render: (row) => (
        <span
          className={`tabular-nums text-sm font-bold ${
            Number(row.balance) > 0 ? "text-amber-700" : "text-emerald-700"
          }`}
        >
          {formatAED(row.balance)}
        </span>
      ),
    },
    {
      key: "payment",
      header: "Payment",
      render: (row) => (
        <span className="text-xs font-medium capitalize text-slate-600">
          {row.paymentmode || "—"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => statusBadge(row.status),
    },
    {
      key: "created",
      header: "Created",
      render: (row) => (
        <span className="text-xs text-slate-500">
          {row.createdAt
            ? moment(row.createdAt).format("DD MMM YYYY · HH:mm")
            : "—"}
        </span>
      ),
    },
  ];

  const renderOrderActions = (row) => (
    <>
      <ActionBtn
        variant="view"
        title="View details"
        onClick={() => handleViewOrder(row)}
      >
        <Eye size={14} />
        View
      </ActionBtn>
      <ActionBtn variant="edit" title="Edit" onClick={() => handleEditUser(row)}>
        <Pencil size={14} />
        Edit
      </ActionBtn>
      <a
        href={`/Invoice/${row._id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="pdt-action-btn pdt-action-btn--edit"
        title="Print invoice"
      >
        <Printer size={14} />
        Print
      </a>
      <ActionBtn
        variant="delete"
        title="Delete"
        onClick={() => handleDeleteUser(row._id)}
      >
        <Trash2 size={14} />
      </ActionBtn>
    </>
  );

  return (
    <div className="space-y-6 pb-10">
      <header className="relative overflow-hidden rounded-2xl border border-violet-200/60 bg-gradient-to-br from-brand-deeper via-brand-dark to-brand px-6 py-7 text-white shadow-[0_20px_50px_-12px_rgba(91,33,182,0.4)] md:px-8">
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-violet-100 ring-1 ring-white/15">
              <ShoppingCart size={14} strokeWidth={2.25} aria-hidden />
              Fulfillment
            </p>
            <h1 className="mt-3 text-2xl font-extrabold tracking-tight md:text-3xl">
              Orders
            </h1>
            <p className="mt-2 max-w-xl text-sm text-violet-100/90">
              Track every order from pick-up to delivery — filter, edit status,
              and print invoices.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/neworder"
              className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-sm font-semibold text-white ring-1 ring-white/25 transition hover:bg-white/15"
            >
              Create order
            </Link>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddUser}
              className="!border-0 !bg-white !text-brand-dark hover:!bg-violet-50"
            >
              Quick add
            </Button>
          </div>
        </div>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { label: "All orders", value: stats.total, icon: Package },
          {
            label: "Filtered revenue",
            value: `${formatAED(stats.revenue)} AED`,
            icon: Wallet,
            isText: true,
          },
          { label: "Unpaid (filtered)", value: stats.unpaid, icon: AlertCircle },
          { label: "Completed (filtered)", value: stats.completed, icon: CheckCircle },
        ].map(({ label, value, icon: Icon, isText }) => (
          <div
            key={label}
            className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-card"
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                {label}
              </p>
              <span className="rounded-lg bg-brand-surface p-2 text-brand">
                <Icon size={18} strokeWidth={2.25} />
              </span>
            </div>
            <p
              className={`mt-2 font-extrabold tabular-nums text-slate-900 ${
                isText ? "text-lg" : "text-2xl"
              }`}
            >
              {value}
            </p>
          </div>
        ))}
      </div>

      <div className="space-y-3 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-card">
        <div
          className="grid grid-cols-2 gap-1.5 rounded-xl bg-slate-100 p-1.5 sm:grid-cols-4"
          role="tablist"
          aria-label="Order status filters"
        >
          {STATUS_FILTERS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={statusFilter === id}
              onClick={() => setStatusFilter(id)}
              className={`rounded-lg px-3 py-2.5 text-sm font-semibold transition ${
                statusFilter === id
                  ? "bg-white text-brand-dark shadow-sm ring-1 ring-slate-200/80"
                  : "text-slate-600 hover:bg-white/60 hover:text-slate-900"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            aria-hidden
          />
          <Input
            placeholder="Search by order reference…"
            value={searchId}
            onChange={(e) => setSearchId(e.target.value)}
            className="!pl-10"
          />
        </div>
      </div>

      <p className="text-sm text-slate-500">
        Showing{" "}
        <span className="font-semibold text-slate-800">
          {filteredData.length}
        </span>{" "}
        orders
        {statusFilter !== "all" ? (
          <span className="text-slate-400"> · {statusFilter}</span>
        ) : null}
      </p>

      <PeopleDataTable
        wide
        columns={orderColumns}
        rows={filteredData}
        rowKey="_id"
        renderActions={renderOrderActions}
        pageSize={10}
        pageSizeOptions={[6, 10, 20, 50]}
        emptyTitle="No orders match your filters"
        emptyHint="Try another status or reference number."
      />

      <Modal
        title={editingUser?._id ? "Edit order" : "Add order"}
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
      >
        <AddForm
          form={form}
          onFinish={handleOk}
          initialValues={editingUser || {}}
        />
      </Modal>

      <Modal
        title={
          viewingOrder ? (
            <span className="flex flex-wrap items-center gap-2">
              <span className="rounded-lg bg-brand-surface px-2 py-0.5 text-sm font-bold text-brand-dark">
                #{viewingOrder.ref}
              </span>
              <span className="text-base font-semibold text-slate-800">
                Order details
              </span>
            </span>
          ) : (
            "Order"
          )
        }
        visible={isViewModalVisible}
        onCancel={handleViewCancel}
        footer={null}
        width={720}
      >
        {viewingOrder && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {statusBadge(viewingOrder.status)}
              <Tag className="m-0 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium capitalize text-slate-700">
                {viewingOrder.paymentmode || "Payment"}
              </Tag>
            </div>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Customer">
                {getName(viewingOrder.userId)}
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {viewingOrder.createdAt
                  ? moment(viewingOrder.createdAt).format("MMM D, YYYY HH:mm")
                  : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Pick-up">
                {convertToReadableDate(viewingOrder.pickTime)}
              </Descriptions.Item>
              <Descriptions.Item label="Delivery">
                {convertToReadableDate(viewingOrder.deliveryDate)}
              </Descriptions.Item>
              <Descriptions.Item label="Address">
                {viewingOrder.deliveryAddress?.address || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Items">
                <div className="space-y-3">
                  {Object.entries(viewingOrder.items || {}).map(([id, item]) => (
                    <div key={id} className="flex items-center gap-3">
                      <Avatar src={item.image} size={48} />
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold capitalize text-slate-800">
                          {item.product}
                        </div>
                        <div className="text-xs text-slate-500">
                          {item.quantity} × {item.price} AED · {item.categoryName}
                        </div>
                      </div>
                      <div className="font-bold tabular-nums text-slate-800">
                        {(item.quantity || 0) * (item.price || 0)} AED
                      </div>
                    </div>
                  ))}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Subtotal">
                {formatAED(viewingOrder.subtotal)} AED
              </Descriptions.Item>
              <Descriptions.Item label="Delivery fee">
                {formatAED(viewingOrder.deliveryFee)} AED
              </Descriptions.Item>
              <Descriptions.Item label="Total">
                <strong>{formatAED(viewingOrder.totalPrice)} AED</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Balance">
                {formatAED(viewingOrder.balance)} AED
              </Descriptions.Item>
              <Descriptions.Item label="Stages">
                {(viewingOrder.stage || []).map((stage, idx) => (
                  <div key={idx} className="text-sm">
                    {stage.title}{" "}
                    <span
                      className={
                        stage.completed ? "text-emerald-600" : "text-amber-600"
                      }
                    >
                      {stage.completed ? "· Done" : "· Pending"}
                    </span>
                    {stage.date ? (
                      <span className="text-slate-400">
                        {" "}
                        ({moment(stage.date).format("MMM D HH:mm")})
                      </span>
                    ) : null}
                  </div>
                ))}
              </Descriptions.Item>
            </Descriptions>
            <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
              <Button onClick={handleViewCancel}>Close</Button>
              <Button
                type="primary"
                onClick={() => {
                  handleViewCancel();
                  handleEditUser(viewingOrder);
                }}
              >
                Edit order
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OrdersPage;
